import type { AnnotationClass } from '@/modules/Editor/AnnotationClass'
import type { IPoint } from '@/modules/Editor/point'
import type { SkeletonPointByPointManager } from '@/modules/Editor/plugins/skeleton/SkeletonPointByPointManager'
import { drawVertex } from '@/modules/Editor/graphicsV2/drawVertex'
import { OCCLUDED_COLOR } from '@/modules/Editor/graphicsV2/drawVerticesByType'
// eslint-disable-next-line boundaries/element-types
import { rgbaString } from '@/uiKit/colorPalette'
import type { View } from '@/modules/Editor/views/view'
import { DEFAULT_VERTEX_SIZE } from '@/modules/Editor/config'
// eslint-disable-next-line boundaries/element-types
import { COMMON_FONT } from '@/modules/Workview/Overlays/overlayEngine/config'

export const drawTemporarySkeletonPointByPoint = (
  ctx: CanvasRenderingContext2D,
  cursorPoint: IPoint,
  annotationClass: AnnotationClass,
  manager: SkeletonPointByPointManager,
  view: View,
): void => {
  if (!annotationClass?.skeletonMetaData) {
    return
  }

  const { edges, nodes: skeletonNodes } = annotationClass.skeletonMetaData
  const placedNodes = manager.getPlacedNodes()
  const obscuredNodes = manager.getObscuredNodes()
  const currentNodeName = manager.getCurrentNodeName()

  const getEdgeColor = (
    edge: { color?: string },
    isObscured: boolean
  ) => isObscured ? rgbaString(OCCLUDED_COLOR) : edge.color ?? annotationClass.colorRGBAstring

  const getNodeColor = (
    nodeColor: string,
    isObscured: boolean
  ) => isObscured ? rgbaString(OCCLUDED_COLOR) : nodeColor

  const drawNodeNameBadge = (
    ctx: CanvasRenderingContext2D,
    point: IPoint,
    nodeName: string,
    color: string,
  ): void => {
    const padding = 4
    const fontSize = 12
    const offsetY = -20

    ctx.font = COMMON_FONT
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    const textMetrics = ctx.measureText(nodeName)
    const textWidth = textMetrics.width
    const textHeight = fontSize

    const badgeX = Math.round(point.x - textWidth / 2 - padding)
    const badgeY = Math.round(point.y + offsetY - textHeight / 2 - padding)
    const badgeWidth = Math.round(textWidth + padding * 2)
    const badgeHeight = Math.round(textHeight + padding * 2)
    const borderRadius = 4

    ctx.fillStyle = color
    ctx.beginPath()
    ctx.roundRect(badgeX, badgeY, badgeWidth, badgeHeight, borderRadius)
    ctx.fill()

    ctx.strokeStyle = color
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.roundRect(badgeX, badgeY, badgeWidth, badgeHeight, borderRadius)
    ctx.stroke()

    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)'
    ctx.shadowBlur = 10
    ctx.lineWidth = 5
    ctx.fillStyle = 'white'
    ctx.fillText(nodeName, Math.round(point.x), Math.round(point.y + offsetY))

    ctx.shadowBlur = 0
    ctx.fillStyle = 'white'
    ctx.fillText(nodeName, Math.round(point.x), Math.round(point.y + offsetY))
  }

  ctx.lineWidth = 1

  // Draw completed edges (between placed nodes)
  for (const edge of edges) {
    const fromNode = placedNodes.get(edge.from)
    const toNode = placedNodes.get(edge.to)

    if (fromNode && toNode) {
      // Both nodes are placed, draw edge
      const isObscured = fromNode.occluded || toNode.occluded
      ctx.strokeStyle = getEdgeColor(edge, isObscured)

      const fromCanvasPoint = view.camera.imageViewToCanvasView(fromNode.point)
      const toCanvasPoint = view.camera.imageViewToCanvasView(toNode.point)

      ctx.beginPath()
      ctx.moveTo(fromCanvasPoint.x, fromCanvasPoint.y)
      ctx.lineTo(toCanvasPoint.x, toCanvasPoint.y)
      ctx.stroke()
    }
  }

  // Draw partial edges (from placed nodes to cursor for next node)
  if (currentNodeName) {
    const nextNodeWillBeObscured = manager.getNextNodeWillBeObscured()

    for (const edge of edges) {
      const fromNode = placedNodes.get(edge.from)
      const toNode = placedNodes.get(edge.to)

      // Draw preview edge if one node is placed and the other is the current node
      if (fromNode && edge.to === currentNodeName) {
        const isObscured = fromNode.occluded || nextNodeWillBeObscured

        ctx.strokeStyle = getEdgeColor(edge, isObscured)
        ctx.setLineDash([5, 5])

        const fromCanvasPoint = view.camera.imageViewToCanvasView(fromNode.point)

        ctx.beginPath()
        ctx.moveTo(fromCanvasPoint.x, fromCanvasPoint.y)
        ctx.lineTo(cursorPoint.x, cursorPoint.y)
        ctx.stroke()
      } else if (toNode && edge.from === currentNodeName) {
        const isObscured = toNode.occluded || nextNodeWillBeObscured

        ctx.strokeStyle = getEdgeColor(edge, isObscured)
        ctx.setLineDash([5, 5])

        const toCanvasPoint = view.camera.imageViewToCanvasView(toNode.point)
        ctx.beginPath()
        ctx.moveTo(cursorPoint.x, cursorPoint.y)
        ctx.lineTo(toCanvasPoint.x, toCanvasPoint.y)
        ctx.stroke()
      }
    }
    ctx.setLineDash([])
  }

  // Draw placed nodes
  for (const [nodeName, placedNode] of placedNodes) {
    const canvasPoint = view.camera.imageViewToCanvasView(placedNode.point)
    const isObscured = obscuredNodes.has(nodeName)
    const nodeColor =
      skeletonNodes.find((node) => node.name === nodeName)?.color ?? annotationClass.colorRGBAstring
    const strokeColor = getNodeColor(nodeColor, isObscured)
    const fillColor = getNodeColor(nodeColor, isObscured)

    drawVertex(ctx, canvasPoint, {
      strokeColor,
      fillColor,
      pointSize: DEFAULT_VERTEX_SIZE,
      lineWidth: 1,
    })
  }

  // Draw current node preview at cursor
  if (currentNodeName) {
    const nextNodeWillBeObscured = manager.getNextNodeWillBeObscured()
    const currentNodeColor =
      skeletonNodes.find((node) => node.name === currentNodeName)?.color ??
      annotationClass.colorRGBAstring
    const strokeColor = getNodeColor(currentNodeColor, nextNodeWillBeObscured)
    const fillColor = getNodeColor('rgba(255, 255, 255, 0.7)', nextNodeWillBeObscured)

    drawVertex(ctx, cursorPoint, {
      strokeColor,
      fillColor,
      pointSize: DEFAULT_VERTEX_SIZE,
      lineWidth: 1,
    })

    drawNodeNameBadge(ctx, cursorPoint, currentNodeName, strokeColor)
  }
}
